import type { Branded } from '@moment/utility-types'

import { z } from 'zod'

/**
 * A day written relative to the current one, in the `T` notation traders use:
 * `T` is today, `T-1` yesterday, `T+2` the day after tomorrow.
 *
 * Carried as the offset itself — whole days from today, negative into the past
 * — so a resolved day is one addition and there is no token left to re-validate
 * downstream. The notation is only the external form: `parseRelativeDay` reads
 * it, `formatRelativeDay` writes it, and everything in between is arithmetic.
 *
 * The offset is what persists, not the day it lands on, which is what lets a
 * saved filter still mean "yesterday" tomorrow.
 *
 * One name, two imports: `import { RelativeDay }` is the schema,
 * `import type { RelativeDay }` is the type.
 *
 * Today is `0`, so test an optional offset with `=== undefined`, never for
 * truthiness.
 */
export type RelativeDay = Branded<number, 'RelativeDay'>

/** Keeps an absurd offset out of the calendar; the notation's five digits. */
const MAX_OFFSET = 99_999

export const RelativeDay = z
  .number()
  .int()
  .min(-MAX_OFFSET)
  .max(MAX_OFFSET)
  .transform((v): RelativeDay => v as RelativeDay)

/** Today — the offset a bare `T` names, and the one every default starts from. */
export const RELATIVE_TODAY: RelativeDay = RelativeDay.parse(0)

/**
 * The bare-notation grammar (`T`, `t-3`, `T+12`) as a regex source, for callers
 * embedding a day inside a larger pattern. Exported so the notation stays
 * defined in exactly one place — see the Views filter grammar in
 * `clients/helpers/date-filter.ts`.
 */
export const RELATIVE_DAY_SOURCE = '[Tt](?:[+-]\\d{1,5})?'

const RELATIVE_DAY_TOKEN = z
  .string()
  .trim()
  .toUpperCase()
  .regex(new RegExp(`^${RELATIVE_DAY_SOURCE}$`))
  .transform((token) => {
    const offset = token === 'T' ? 0 : Number(token.slice(1))
    // `Number('-0')` is -0, a value `Object.is` — and so `toBe`, `Map`, and
    // `Set` — keeps apart from the 0 every other spelling of today produces.
    return offset === 0 ? 0 : offset
  })
  .pipe(RelativeDay)

/**
 * The offset a piece of free-typed or persisted text names, or `null` when it
 * isn't the notation. Case and padding canonicalize away into the number, so
 * `t-05` and `T-5` are both `-5`, and `T-0` is `0`.
 */
export function parseRelativeDay(value: string): RelativeDay | null {
  const parsed = RELATIVE_DAY_TOKEN.safeParse(value)
  return parsed.success ? parsed.data : null
}

/** The canonical token for an offset: `T`, `T-1`, `T+3`. */
export function formatRelativeDay(day: RelativeDay): string {
  if (day === 0) {
    return 'T'
  }
  return day < 0 ? `T${day}` : `T+${day}`
}

/** The calendar unit a {@link RelativeDate} counts in. */
export const RelativeDateUnit = z.enum(['day', 'month', 'year'])
export type RelativeDateUnit = z.infer<typeof RelativeDateUnit>

/**
 * A calendar date written relative to today: `offset` whole units, negative
 * into the past. A month or year step keeps the day of month, clamped to the
 * target month's last day (Jan 31 plus one month is Feb 28), which is what
 * ClickHouse's `addMonths` does too.
 *
 * A bare number is the persisted form of a day offset — the only form that
 * existed before units — so filters saved then still read as the same intent.
 */
export interface RelativeDate {
  unit: RelativeDateUnit
  offset: number
}

/** Month and year reaches keep the resolved year to the four digits `IsoDateStr` reads. */
const MAX_OFFSET_BY_UNIT: Record<RelativeDateUnit, number> = {
  day: MAX_OFFSET,
  month: 1200,
  year: 100,
}

export const RelativeDate = z.union([
  RelativeDay.transform((offset): RelativeDate => ({ unit: 'day', offset })),
  z
    .object({ unit: RelativeDateUnit, offset: z.number().int() })
    .refine(
      ({ unit, offset }) => Math.abs(offset) <= MAX_OFFSET_BY_UNIT[unit],
      'offset past what its unit reaches',
    ),
])

/** A day offset as the {@link RelativeDate} a filter bound carries. */
export function relativeDays(day: RelativeDay): RelativeDate {
  return { unit: 'day', offset: day }
}
